import React, { useState } from 'react';
import { Table, Button, Spinner, Modal, Form, Pagination } from 'react-bootstrap';
import { useQuery, useQueryClient } from 'react-query';
import { Get, Put } from '@app/utils/httpProvider';
import { ACCOUNT_AUTHINFO_API, ACCOUNT_API, BaseGatewayImage, DANHSACH_DUYET_NGOAINGU_API, DONVI_API } from '@app/utils/apiUrl';
import { toast } from 'react-toastify';
import { getUser } from '@app/utils/oidc';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';
import Select from 'react-select';

// Helper Format Ngày
const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('vi-VN');
};

// Helper Format Loại văn bằng
const formatLoaiVanBang = (type) => {
    if (!type) return '-';
    const strType = String(type).trim();
    if (strType === 'NuocNgoai') return 'Văn bằng nước ngoài';
    if (strType === 'VanBangTrongNuoc') return 'Văn bằng trong nước (ngoại ngữ)';
    return strType;
};

const DuyetVanBangChungChi = () => {
    const queryClient = useQueryClient();
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;
    const [selectedDonVi, setSelectedDonVi] = useState('');

    // Fetch danh sách đơn vị từ danh mục dùng chung (cực kỳ nhẹ, không ảnh hưởng performance)
    const { data: donViRes } = useQuery(['danhMucDonViAll'], async () => {
        const res = await Get(DONVI_API.GET_PAGING, null, { PageSize: 10000 });
        return res?.data || res;
    });

    const donViOptions = React.useMemo(() => {
        const list = Array.isArray(donViRes) ? donViRes : (donViRes?.data || []);
        return list.map(item => {
            const ten = item.tenDonVi || item.TenDonVi;
            return ten ? { value: ten, label: ten } : null;
        }).filter(Boolean);
    }, [donViRes]);

    // State cho modal xác nhận duyệt
    const [openConfirmDuyet, setOpenConfirmDuyet] = useState(false);
    const [openReasonModal, setOpenReasonModal] = useState(false);
    const [reasonText, setReasonText] = useState('');
    const [confirmAction, setConfirmAction] = useState(null); // { item, field, value, label }

    // Hàm fetch dữ liệu và nhóm theo AccountId
    const fetchAllData = async () => {
        try {
            const params = {
                pageIndex: currentPage,
                pageSize: itemsPerPage,
                keyword: "",
                donVi: selectedDonVi,
            };

            const vanBangRes = await Get(DANHSACH_DUYET_NGOAINGU_API.GET_ALL_VANBANG_BY_PAGING, null, params);

            const vanBangItems = (vanBangRes?.data?.$values || vanBangRes?.data?.data || (Array.isArray(vanBangRes?.data) ? vanBangRes.data : [])).map(item => ({ ...item, type: 'vanBang' }));

            // Lọc bỏ Nhập liệu
            const combinedItems = vanBangItems.filter(item => {
                const status = item.trangThai ?? item.TrangThai;
                if (status === null || status === undefined) return true;
                if (!isNaN(status)) return Number(status) !== 0;
                return status !== "NhapLieu";
            });

            // Nhóm theo AccountId
            const groupedMap = {};
            combinedItems.forEach(item => {
                const accId = item.accountId || item.AccountId;
                if (!groupedMap[accId]) {
                    groupedMap[accId] = {
                        accountId: accId,
                        items: []
                    };
                }
                groupedMap[accId].items.push(item);
            });

            const uniqueAccIds = Object.keys(groupedMap);

            // Fetch profile cho từng AccountId duy nhất
            const profiles = await Promise.all(uniqueAccIds.map(async (accId) => {
                try {
                    const [authRes, detailRes] = await Promise.all([
                        Get(`${ACCOUNT_AUTHINFO_API.GET_BY_ID_ACCOUNT}/${accId}`),
                        Get(`${ACCOUNT_API.GET_ACCOUNT_INFO_BYID_FROM_APP_DUNGCHUNG}/${accId}`)
                    ]);

                    const auth = authRes?.data || authRes;
                    const detail = detailRes?.data || detailRes;

                    const fullName = auth ? [
                        auth.lastName || auth.LastName,
                        auth.middleName || auth.MiddleName,
                        auth.firstName || auth.FirstName
                    ].filter(v => v && String(v).trim() !== "").join(' ') : '-';

                    const dob = auth?.dateOfBirth || auth?.DateOfBirth;
                    const yearOfBirth = dob ? new Date(dob).getFullYear() : '-';
                    const chucDanh = detail?.doiTuong?.tenDoiTuong || detail?.DoiTuong?.TenDoiTuong || '-';
                    const donVis = detail?.donVis || detail?.DonVis?.$values || detail?.DonVis || [];
                    const khoa = (Array.isArray(donVis) && donVis[0]) ? (donVis[0].tenDonVi || donVis[0].TenDonVi) : '-';

                    return { accId, fullName, yearOfBirth, chucDanh, khoa };
                } catch (e) {
                    return { accId, fullName: '-', yearOfBirth: '-', chucDanh: '-', khoa: '-' };
                }
            }));

            // Trộn data profile vào Map
            profiles.forEach(p => {
                if (groupedMap[p.accId]) {
                    groupedMap[p.accId] = { ...groupedMap[p.accId], ...p };
                }
            });

            const totalRecords = vanBangRes?.totalRecords || vanBangRes?.TotalRecords || vanBangRes?.totalRow || vanBangRes?.TotalRow || vanBangRes?.data?.totalRecords || vanBangRes?.data?.TotalRecords || 0;

            return {
                items: Object.values(groupedMap),
                totalRecords: totalRecords
            };
        } catch (error) {
            console.error('Lỗi lấy dữ liệu duyệt:', error);
            throw error;
        }
    };

    const { data: listData, isLoading, refetch } = useQuery(
        ['duyetVanBangChungChi', currentPage, selectedDonVi],
        fetchAllData,
        { cacheTime: 0, staleTime: 0 }
    );

    const handleUpdateStatus = async (item, field, value) => {
        const queryKey = ['duyetVanBangChungChi', currentPage, selectedDonVi];
        const itemId = item.id || item.Id || item.vanBangId || item.chungChiNgoaiNguId;

        // Optimistic Update trong cấu trúc dữ liệu mới (nested)
        queryClient.setQueryData(queryKey, (oldData) => {
            if (!oldData) return { items: [], totalRecords: 0 };
            return {
                ...oldData,
                items: (oldData.items || []).map((employee) => ({
                    ...employee,
                    items: employee.items.map((it) => {
                        const itId = it.id || it.Id || it.vanBangId || it.chungChiNgoaiNguId;
                        if (itId === itemId) {
                            return {
                                ...it,
                                [field]: value,
                                [field.charAt(0).toLowerCase() + field.slice(1)]: value
                            };
                        }
                        return it;
                    })
                }))
            };
        });

        try {
            const apiPath = item.type === 'vanBang' ? DANHSACH_DUYET_NGOAINGU_API.UPDATE_FIELD_VAN_BANG : DANHSACH_DUYET_NGOAINGU_API.UPDATE_FIELD_CHUNG_CHI_NGOAI_NGU;
            const payload = { id: itemId, fieldName: field, value: value };

            const res = await Put(apiPath, null, payload);
            if (res) {
                toast.success('Cập nhật trạng thái thành công');
                queryClient.invalidateQueries(queryKey);
                queryClient.invalidateQueries(['thongKeNgoaiNgu']);
            }
        } catch (error) {
            toast.error('Lỗi cập nhật trạng thái');
            refetch();
        }
    };

    const openConfirmAction = (item, field, value, label) => {
        setConfirmAction({ item, field, value, label });
        // Nếu là Không duyệt (TrangThai = 3) hoặc Không đạt (XacNhanDatYeuCau = 'Không đạt') thì mở Modal lý do
        if ((field === 'TrangThai' && value === 3) || (field === 'XacNhanDatYeuCau' && value === 'Không đạt')) {
            setReasonText('');
            setOpenReasonModal(true);
        } else {
            setOpenConfirmDuyet(true);
        }
    };

    const handleReasonSubmit = async () => {
        if (!reasonText.trim()) {
            toast.error('Vui lòng nhập lý do không phù hợp');
            return;
        }
        if (!confirmAction) return;

        const { item, field, value } = confirmAction;
        setOpenReasonModal(false);

        // Gọi cập nhật trạng thái trước
        await handleUpdateStatus(item, field, value);

        // Sau đó gọi cập nhật field lý do tương ứng (LyDoKhongDuyet hoặc LyDoKhongDat)
        try {
            const apiPath = item.type === 'vanBang' ? DANHSACH_DUYET_NGOAINGU_API.UPDATE_FIELD_VAN_BANG : DANHSACH_DUYET_NGOAINGU_API.UPDATE_FIELD_CHUNG_CHI_NGOAI_NGU;
            const itemId = item.id || item.Id || item.vanBangId || item.chungChiNgoaiNguId;

            const reasonFieldName = (field === 'TrangThai') ? 'LyDoKhongDuyet' : 'LyDoKhongDat';

            const payload = { id: itemId, fieldName: reasonFieldName, value: reasonText };

            await Put(apiPath, null, payload);
        } catch (error) {
            console.error('Lỗi cập nhật lý do:', error);
        }

        setConfirmAction(null);
    };

    const handleConfirmAccept = async () => {
        if (!confirmAction) return;
        const { item, field, value } = confirmAction;
        setOpenConfirmDuyet(false);

        // Thực hiện cập nhật trạng thái chính
        await handleUpdateStatus(item, field, value);

        // Nếu là Duyệt (TrangThai = 2), reset LyDoKhongDuyet về null
        // Nếu là Đạt (XacNhanDatYeuCau = 'Đạt'), reset LyDoKhongDat về null
        if ((field === 'TrangThai' && value === 2) || (field === 'XacNhanDatYeuCau' && value === 'Đạt')) {
            try {
                const apiPath = item.type === 'vanBang' ? DANHSACH_DUYET_NGOAINGU_API.UPDATE_FIELD_VAN_BANG : DANHSACH_DUYET_NGOAINGU_API.UPDATE_FIELD_CHUNG_CHI_NGOAI_NGU;
                const itemId = item.id || item.Id || item.vanBangId || item.chungChiNgoaiNguId;

                const reasonFieldName = (field === 'TrangThai') ? 'LyDoKhongDuyet' : 'LyDoKhongDat';

                const payload = { id: itemId, fieldName: reasonFieldName, value: null };
                await Put(apiPath, null, payload);
            } catch (error) {
                console.error('Lỗi reset lý do:', error);
            }
        }

        setConfirmAction(null);
    };

    const handleConfirmCancel = () => {
        setOpenConfirmDuyet(false);
        setConfirmAction(null);
    };

    const handleOpenFile = (path) => {
        if (!path) return;
        const fullPath = path.startsWith('http') ? path : `${BaseGatewayImage}${path}`;
        window.open(fullPath, '_blank');
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Đang tải dữ liệu và thông tin nhân viên...</p>
            </div>
        );
    }

    const totalRecords = listData?.totalRecords || 0;
    const totalPages = Math.ceil(totalRecords / itemsPerPage);
    const displayedEmployees = listData?.items || [];

    return (
        <div className="py-2">
            {/* Bộ lọc đơn vị */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h5 className="fw-bold text-dark mb-0">Danh sách văn bằng chờ duyệt</h5>
                <div className="d-flex align-items-center gap-2" style={{ minWidth: 320, maxWidth: 450 }}>
                    <span className="text-nowrap fw-semibold text-muted" style={{ fontSize: '0.9rem' }}>Lọc theo đơn vị:</span>
                    <div style={{ flex: 1 }}>
                        <Select
                            options={donViOptions}
                            value={selectedDonVi ? { value: selectedDonVi, label: selectedDonVi } : null}
                            onChange={(opt) => {
                                setSelectedDonVi(opt ? opt.value : '');
                                setCurrentPage(1);
                            }}
                            placeholder="-- Tất cả đơn vị --"
                            isClearable
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderRadius: 8,
                                    fontSize: '0.9rem',
                                    border: '1px solid #dee2e6',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        border: '1px solid #ced4da'
                                    }
                                }),
                                menu: (base) => ({
                                    ...base,
                                    fontSize: '0.9rem',
                                    zIndex: 999
                                })
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="table-responsive">
                <Table bordered className="align-middle custom-approval-table">
                    <thead className="table-light">
                        <tr className="text-center text-nowrap">
                            <th style={{ width: '50px' }}>STT</th>
                            <th style={{ minWidth: '180px' }}>Họ và tên</th>
                            <th style={{ width: '100px' }}>Năm sinh</th>
                            <th style={{ minWidth: '110px' }}>Chức danh</th>
                            <th style={{ minWidth: '180px' }}>Đơn vị</th>
                            <th style={{ minWidth: '160px' }}>Tên văn bằng</th>
                            <th style={{ minWidth: '160px' }}>Loại văn bằng</th>
                            <th style={{ minWidth: '140px' }}>Chuyên ngành</th>
                            <th style={{ minWidth: '180px' }}>Cơ sở cấp bằng</th>
                            <th style={{ minWidth: '120px' }}>Quốc gia cấp</th>
                            <th style={{ minWidth: '120px' }}>Số hiệu</th>
                            <th style={{ minWidth: '120px' }}>Ngày cấp</th>
                            <th style={{ minWidth: '100px' }}>Xếp loại</th>
                            <th style={{ minWidth: '120px' }}>Hợp pháp hóa</th>
                            <th style={{ minWidth: '100px' }}>Minh chứng</th>
                            <th style={{ minWidth: '140px' }}>Duyệt văn bằng</th>
                            <th style={{ minWidth: '140px' }}>Xác nhận Đạt yêu cầu</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayedEmployees.length > 0 ? displayedEmployees.map((employee, index) => {
                            const stt = (currentPage - 1) * itemsPerPage + index + 1;
                            const rowSpan = employee.items.length;

                            return (
                                <React.Fragment key={employee.accountId || index}>
                                    {employee.items.map((item, idx) => {
                                        const tenHienThi = item.tenVanBang || item.TenVanBang || item.tenChungChi || item.TenChungChi || '-';
                                        const filePath = item.fileMinhChungPath || item.FileMinhChungPath;

                                        const parseTrangThai = (val) => {
                                            if (val === null || val === undefined) return -1;
                                            if (!isNaN(val)) return Number(val);
                                            const enumMap = { "NhapLieu": 0, "ChoDuyet": 1, "DaDuyet": 2, "TuChoi": 3 };
                                            return enumMap[String(val).trim()] ?? -1;
                                        };

                                        const statusDuyet = parseTrangThai(item.trangThai ?? item.TrangThai);
                                        const statusDat = item.xacNhanDatYeuCau ?? item.XacNhanDatYeuCau ?? null;

                                        return (
                                            <tr key={item.id || `${employee.accId}-${idx}`}>
                                                {idx === 0 && (
                                                    <>
                                                        <td rowSpan={rowSpan} className="text-center" style={{ color: '#000' }}>{stt}</td>
                                                        <td rowSpan={rowSpan} className="fw-bold text-primary" style={{ color: '#007bff' }}>{employee.fullName || '-'}</td>
                                                        <td rowSpan={rowSpan} className="text-center" style={{ color: '#000' }}>{employee.yearOfBirth || '-'}</td>
                                                        <td rowSpan={rowSpan} style={{ color: '#000' }}>{employee.chucDanh || '-'}</td>
                                                        <td rowSpan={rowSpan} style={{ color: '#000' }}>{employee.khoa || '-'}</td>
                                                    </>
                                                )}
                                                <td style={{ color: '#000' }}>{item.tenVanBang || item.TenVanBang || '-'}</td>
                                                <td style={{ color: '#000' }}>{formatLoaiVanBang(item.loaiVanBang || item.LoaiVanBang)}</td>
                                                <td style={{ color: '#000' }}>{item.chuyenNganh || item.ChuyenNganh || '-'}</td>
                                                <td style={{ color: '#000' }}>{item.coSoCapBang || item.CoSoCapBang || '-'}</td>
                                                <td style={{ color: '#000' }}>{item.quocGiaCap || item.QuocGiaCap || '-'}</td>
                                                <td style={{ color: '#000' }}>{item.soHieuVanBang || item.SoHieuVanBang || '-'}</td>
                                                <td className="text-center" style={{ color: '#000' }}>{formatDate(item.ngayCap || item.NgayCap)}</td>
                                                <td className="text-center" style={{ color: '#000' }}>{item.xepLoai || item.XepLoai || '-'}</td>
                                                <td className="text-center" style={{ color: '#000' }}>
                                                    {(item.isHopPhapHoa || item.IsHopPhapHoa) ? (
                                                        <span className="badge bg-success">Có</span>
                                                    ) : (
                                                        <span className="badge bg-secondary">Không</span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    {filePath ? (
                                                        <Button variant="outline-info" size="sm" onClick={() => handleOpenFile(filePath)}>
                                                            Xem
                                                        </Button>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex justify-content-center gap-2">
                                                        <Button variant={statusDuyet === 2 ? 'success' : 'outline-success'} size="sm" className="btn-icon-square"
                                                            onClick={() => openConfirmAction(item, 'TrangThai', 2, `Xác nhận duyệt "${tenHienThi}"?`)} title="Duyệt">
                                                            <i className="fas fa-check"></i>
                                                        </Button>
                                                        <Button variant={statusDuyet === 3 ? 'danger' : 'outline-danger'} size="sm" className="btn-icon-square"
                                                            onClick={() => openConfirmAction(item, 'TrangThai', 3, `Xác nhận không duyệt "${tenHienThi}"?`)} title="Không duyệt">
                                                            <i className="fas fa-times"></i>
                                                        </Button>
                                                    </div>
                                                </td>
                                                <td className="text-center" style={{ color: '#000' }}>
                                                    <div className="d-flex justify-content-center gap-2">
                                                        <Button variant={(statusDat === 'Đạt') && statusDat !== null ? 'success' : 'outline-success'} size="sm" className="btn-icon-square"
                                                            onClick={() => openConfirmAction(item, 'XacNhanDatYeuCau', 'Đạt', 'Xác nhận đạt yêu cầu?')} title="Đạt">
                                                            <i className="fas fa-check"></i>
                                                        </Button>
                                                        <Button variant={(statusDat === 'Không đạt') && statusDat !== null ? 'danger' : 'outline-danger'} size="sm" className="btn-icon-square"
                                                            onClick={() => openConfirmAction(item, 'XacNhanDatYeuCau', 'Không đạt', 'Xác nhận không đạt yêu cầu?')} title="Không đạt">
                                                            <i className="fas fa-times"></i>
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        }) : (
                            <tr><td colSpan="17" className="text-center py-4 text-muted">Không có dữ liệu chờ duyệt</td></tr>
                        )}
                    </tbody>
                </Table>
            </div>

            {/* Pagination Component */}
            {totalPages > 1 && (
                <div className="d-flex justify-content-end mt-3">
                    <Pagination className="custom-pagination">
                        <Pagination.First disabled={currentPage === 1} onClick={() => setCurrentPage(1)} />
                        <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} />
                        {[...Array(totalPages)].map((_, idx) => (
                            <Pagination.Item
                                key={idx + 1}
                                active={idx + 1 === currentPage}
                                onClick={() => setCurrentPage(idx + 1)}
                            >
                                {idx + 1}
                            </Pagination.Item>
                        ))}
                        <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} />
                        <Pagination.Last disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)} />
                    </Pagination>
                </div>
            )}

            <ConfirmModal isOpen={openConfirmDuyet} confirmType="confirm" handleAccept={handleConfirmAccept} handleCancel={handleConfirmCancel}
                content={confirmAction?.label || 'Bạn có chắc chắn muốn thực hiện thao tác này?'} />

            {/* Modal Lý do không phù hợp */}
            <Modal
                show={openReasonModal}
                onHide={() => setOpenReasonModal(false)}
                centered
                className="reason-modal"
                style={{ zIndex: 1060 }}
                backdrop="static"
            >
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="d-flex align-items-center text-danger fw-bold h5">
                        <i className="fas fa-exclamation-triangle me-2"></i> Lý do không phù hợp
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <Form.Group>
                        <Form.Label className="fw-bold">Chọn lý do <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                            as="input"
                            placeholder="-- Vui lòng nhập lý do --"
                            value={reasonText}
                            onChange={(e) => setReasonText(e.target.value)}
                            className="py-2"
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0 justify-content-end pt-0">
                    <Button variant="light" onClick={() => setOpenReasonModal(false)} className="px-4 border">
                        Hủy
                    </Button>
                    <Button variant="primary" onClick={handleReasonSubmit} className="px-4 bg-primary border-0" style={{ background: '#58a5ff' }}>
                        Tiếp tục
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .custom-approval-table { border: 1px solid #dee2e6; font-size: 0.9rem; }
                .custom-approval-table thead th { background-color: #f8f9fa; color: #495057; font-weight: 700; border: 1px solid #dee2e6 !important; border-bottom: 2px solid #dee2e6 !important; padding: 12px 8px; text-align: center; }
                .custom-approval-table tbody td { padding: 12px 8px; vertical-align: middle; text-align: center; background-color: #fff; border: 1px solid #dee2e6; }
                .btn-icon-square { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; padding: 0; border-radius: 3px; }
                
                .reason-modal .modal-content { border-radius: 12px; border: none; box-shadow: 0 5px 15px rgba(0,0,0,.2); }
                .reason-modal .modal-header .btn-close { font-size: 0.8rem; }
                .reason-modal .form-control:focus { box-shadow: none; border-color: #dee2e6; }
                
                /* Pagination styling */
                .custom-pagination .page-link {
                    color: #0d6efd;
                    border-radius: 6px;
                    margin: 0 3px;
                    border: 1px solid #dee2e6;
                    padding: 6px 12px;
                    font-weight: 500;
                    transition: all 0.2s ease;
                }
                .custom-pagination .page-item.active .page-link {
                    background-color: #0d6efd;
                    border-color: #0d6efd;
                    color: #fff;
                }
                .custom-pagination .page-item.disabled .page-link {
                    color: #6c757d;
                    background-color: #fff;
                    border-color: #dee2e6;
                }
                .custom-pagination .page-link:hover {
                    background-color: #e9ecef;
                    border-color: #dee2e6;
                }

                /* Đảm bảo modal luôn nằm trên cùng */
                .modal-open .modal { z-index: 1060 !important; }
                .modal-backdrop { z-index: 1050 !important; }
            `}</style>
        </div>
    );
};

export default DuyetVanBangChungChi;